import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function AiSummaryPanel({ cid, canEdit, embedded = false }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/consultations/${cid}/ai-summary`);
      setData(r.data);
      setDraft(r.data?.ai_summary || "");
    } catch (error) { console.warn("[ai-summary] load failed", error?.response?.status || error?.message || error); }
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  // Poll job status while a job is queued/running.
  const jobActive = job?.state === "queued" || job?.state === "running";
  useEffect(() => {
    if (!canEdit) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(`/consultations/${cid}/ai-summary/status`);
        if (cancelled) return;
        setJob(r.data?.job || null);
        if (r.data?.ai_summary) {
          setData((d) => ({ ...(d || {}), ai_summary: r.data.ai_summary, available: true,
            finalized: !!r.data.ai_summary_finalized }));
          setDraft(r.data.ai_summary);
        }
        if (r.data?.job?.state === "completed" || r.data?.job?.state === "failed") {
          setBusy(false);
        }
      } catch (error) { console.warn("[ai-summary] status poll failed", error?.response?.status || error?.message || error); }
    };
    poll();
    const id = setInterval(() => { if (jobActive) poll(); }, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [cid, canEdit, jobActive]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/consultations/${cid}/ai-summary/enqueue`);
      setJob({ id: r.data.job_id, state: r.data.state });
      toast.success("Generation queued — this can take ~30-60s for GPT-5");
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); setBusy(false); }
  };

  const save = async (finalize = false) => {
    setBusy(true);
    try {
      const r = await api.patch(`/consultations/${cid}/ai-summary`, { ai_summary: draft, finalize });
      setData((d) => ({ ...(d || {}), ai_summary: r.data.ai_summary, finalized: r.data.ai_summary_finalized, available: true }));
      setEditing(false);
      toast.success(finalize ? "Summary shared with client" : "Draft saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const hasText = !!(data?.ai_summary);
  const isRunning = job && (job.state === "queued" || job.state === "running");
  if (!canEdit && !data?.available) return null;

  return (
    <div className={`w-full max-w-full min-w-0 ${embedded ? "" : "bg-white border border-slate-200 rounded-2xl p-5"}`} data-testid="ai-summary-panel">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Sparkles size={16} className="text-[#D4AF37]" />
          <p className="text-sm font-bold text-[#0A2540]">AI Consultation Memo</p>
        </div>
        {data?.finalized && (
          <span className="max-w-full break-words text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="ai-summary-finalized-pill">Shared with client</span>
        )}
      </div>

      {isRunning && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-800 flex items-center gap-2" data-testid="ai-summary-job-running">
          <Loader2 size={12} className="animate-spin" />
          {job.state === "queued" ? "Queued — starting GPT-5…" : "GPT-5 drafting your memo…"}
        </div>
      )}
      {job?.state === "failed" && (
        <p className="mt-2 text-xs text-rose-600" data-testid="ai-summary-job-error">Generation failed: {job.error || "try again"}</p>
      )}

      {!hasText && canEdit && !isRunning && (
        <div className="mt-3">
          <p className="text-xs text-slate-500 mb-2">Draft a polished memo from the chat transcript using GPT-5. Runs in the background — takes about 30-60 seconds.</p>
          <Button onClick={generate} disabled={busy} data-testid="ai-summary-generate-btn" className="w-full adv-btn-primary rounded-xl h-9">
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <FileText size={14} className="mr-1" />} Generate draft
          </Button>
        </div>
      )}

      {hasText && (
        <div className="mt-3 space-y-2">
          {editing ? (
            <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={12} className="text-xs rounded-xl font-mono" data-testid="ai-summary-textarea" />
          ) : (
            <pre className="text-xs whitespace-pre-wrap text-slate-700 max-h-72 overflow-y-auto bg-slate-50 rounded-xl p-3 border border-slate-100" data-testid="ai-summary-preview">{data.ai_summary}</pre>
          )}
          {canEdit && (
            <div className="flex flex-wrap gap-2">
              {!editing && (
                <Button onClick={() => setEditing(true)} variant="outline" className="rounded-xl text-xs h-8" data-testid="ai-summary-edit-btn">Edit</Button>
              )}
              {editing && (
                <>
                  <Button onClick={() => save(false)} disabled={busy} variant="outline" className="rounded-xl text-xs h-8" data-testid="ai-summary-save-btn">Save draft</Button>
                  <Button onClick={() => save(true)} disabled={busy} className="rounded-xl adv-btn-gold text-xs h-8" data-testid="ai-summary-finalize-btn">Share with client</Button>
                </>
              )}
              <Button onClick={generate} disabled={busy || isRunning} variant="outline" className="rounded-xl text-xs h-8" data-testid="ai-summary-regenerate-btn">Regenerate</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
